import {
  IValidateMedicineExpiryRequest,
  IOderManagementActionBody,
  IOrderManagementFilterBody,
  IOrderManagementStatsFilterBody,
} from "@/api_framework/api_modals/Orders";
import SimpleCheckboxWithError from "@/app/components/ui/CheckBoxes/SimpleCheckboxWithError";
import Input from "@/app/components/ui/Inputs/Input";
import { useOrderManagementOrderListAPI } from "@/app/hooks/api_hooks/orders/useOrderListAPI";
import { useOrderManagementOrderUpdateAPI } from "@/app/hooks/api_hooks/orders/useOrderManagementActionAPI";
import { useOrderManagementExaclationAPI } from "@/app/hooks/api_hooks/orders/useOrderManagementExaclationAPI";
import { useOrderManagementStatsAPI } from "@/app/hooks/api_hooks/orders/useOrderStatsAPI";
import { useValidateMedicineExpiryAPI } from "@/app/hooks/api_hooks/orders/useValidateMedicineExpiryAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import {
  IOrderManagementState,
  setIsUserPopupModalActive,
} from "@/app_redux/reducers/slices/auth/order_management_slice";
import { Dialog, Transition } from "@headlessui/react";
import { debounce } from "lodash";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import ImgPreview from "./ImgPreview";
import GiftCardRow from "./GiftCardRow";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router";
import ExpiryRepeatWarningModal from "@/app/components/routes/modals/ExpiryRepeatWarningModal";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

export interface TAX {
  tax_name: string;
  tax_rate: string;
}

export interface QCItemPropsInterface {
  vetic_variant_id: string;
  quantity_to_pick: number;
  item_name: string;
  mrp: number;
  mrp_check?: string | null;
  lot_number?: string | null;
  lot_no?: string | null;
  lot?: string | null;
  price: number;
  item_dimensions: {
    item_weight: number;
    item_weight_unit_of_measure: string;
  };
  package_dimensions: {
    "Package weight": number;
    "Package weight unit of measurement": string;
  };
  thumbnail: string;
  tax_details: TAX[];
  bar_code: string | string[] | null;
  inputqty: string | any;
  inputbarcode: string | null;
  inputexpiry?: string | null;
  inputexpiries?: string[];
  checkbox: boolean;
  is_gift?: boolean | string | null;
}

export interface IQCPackpropsInterface {
  products: QCItemPropsInterface[];
  expiry: boolean;
  lotNo: boolean;
  partial: boolean;
  match?: boolean;
  scanner?: string;
}

interface IPackButton {
  open: boolean;
  setOpen: (open: boolean) => void;
  orders: any;
  isDayClosing?: boolean;
}

interface IRepeatExpiryConfirmationState {
  id: number;
  signature: string;
  payload: IValidateMedicineExpiryRequest;
}

const normalizeBarcode = (value: string | string[] | null): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .filter((b) => typeof b === "string")
      .map((b) => b.trim())
      .filter((b) => b.length > 0);
  }
  const trimmed = value.trim();
  return trimmed ? [trimmed] : [];
};

const formatExpiryDateInput = (value: string, addTrailingSeparators = true): string => {
  const digitsOnly = value.replace(/\D/g, "").slice(0, 8);
  if (digitsOnly.length <= 1) return digitsOnly;
  if (digitsOnly.length === 2) return addTrailingSeparators ? `${digitsOnly}/` : digitsOnly;
  if (digitsOnly.length <= 3) return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
  if (digitsOnly.length === 4) {
    const ddmm = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}`;
    return addTrailingSeparators ? `${ddmm}/` : ddmm;
  }
  if (digitsOnly.length <= 7) return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
  return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
};
const isGiftItem = (item: any): boolean => item?.is_gift === true || item?.is_gift === "true";

// Delivery pincode prefixes the gift card is live in: Delhi (110), Gurugram (122), Noida/Ghaziabad (201).
// Pincode, not address city: the city text is free-form and often wrong or empty.
const GIFT_CARD_PINCODE_PREFIXES = ["110", "122", "201"];

const isGiftCardPincode = (pincode?: string | number | null): boolean => {
  const normalizedPincode = String(pincode ?? "").replace(/\s/g, "");
  return /^\d{6}$/.test(normalizedPincode) && GIFT_CARD_PINCODE_PREFIXES.some((prefix) => normalizedPincode.startsWith(prefix));
};

const EXPIRY_DATE_REGEX = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;

const hasInvalidExpiryParts = (value?: string | null): boolean => {
  const normalizedValue = String(value || "");
  const parts = normalizedValue.split("/");
  const dayRaw = (parts[0] || "").replace(/\D/g, "");
  const monthRaw = (parts[1] || "").replace(/\D/g, "");

  if (dayRaw.length === 2) {
    const day = Number(dayRaw);
    if (day < 1 || day > 31) return true;
  }

  if (monthRaw.length === 2) {
    const month = Number(monthRaw);
    if (month < 1 || month > 12) return true;
  }

  return false;
};

const PackButtonComponent: React.FC<IPackButton> = ({ open, setOpen, orders, isDayClosing }) => {
  const [imageitem, setImageitem] = useState(null);

  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState } = useRouteInfo(routeId);

  const { orderFilter, orderModule } = routeState.state as IOrderManagementState;
  const [isShowPertialOrder, setsIShowPertialOrder] = useState<boolean>(false);
  const [highlightedRows, setHighlightedRows] = useState<number[]>([]);
  const [scannedItems, setScannedItems] = useState(new Set()); // Track which items have been scanned
  const [lastScannedIndex, setLastScannedIndex] = useState(null); // Track last scanned item index
  const [validatedExpiryByIndex, setValidatedExpiryByIndex] = useState<Record<number, boolean>>({});
  const [validatingExpiryByIndex, setValidatingExpiryByIndex] = useState<Record<number, boolean>>({});
  const [repeatExpiryConfirmation, setRepeatExpiryConfirmation] = useState<IRepeatExpiryConfirmationState | null>(null);
  const [isRepeatConfirming, setIsRepeatConfirming] = useState(false);
  const [giftCardQty, setGiftCardQty] = useState<string>("");
  const attemptedExpirySignatureRef = useRef<Record<number, string>>({});
  const scrollContainerRef = useRef(null);

  const hasGiftItem =
    Boolean(orders?.order_details?.sub_order_items?.some(isGiftItem)) &&
    isGiftCardPincode(orders?.client_details?.address_detail?.pincode);

  const { execute: updateOrdersAPI } = useOrderManagementOrderUpdateAPI();
  const { execute: exclationOrdersAPI } = useOrderManagementExaclationAPI();
  const { execute: validateMedicineExpiryAPI } = useValidateMedicineExpiryAPI();
  const { execute: logClickAPI } = useClickLogAPI();
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);

  const logGiftCardQtyClick = () => {
    logClickAPI({
      event_type: "GIFT_CARD_QTY_CLICKED",
      scr_id: "order_management_pack",
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
      order_id: orders?.order_details?.order_id,
      sub_order_id: orders?.order_details?.sub_order_id,
      clinic_id: orders?.clinic_details?.id,
      clinic_name: orders?.clinic_details?.name,
      gift_card_qty: giftCardQty === "" ? null : parseInt(giftCardQty),
    });
  };
  const handleClose = () => {
    // If repeat-expiry confirmation is open, only that modal should close.
    // Prevent parent Pack modal from closing due nested dialog onClose events.
    if (repeatExpiryConfirmation) return;
    setRepeatExpiryConfirmation(null);
    setIsRepeatConfirming(false);
    setOpen(false);
  };

  const fetchOrderListAPI = useOrderManagementOrderListAPI();
  const fetchStatsAPI = useOrderManagementStatsAPI();

  useEffect(() => {
    setImageitem(orders?.order_details?.sub_order_items?.[0] ?? null);
  }, [orders?.order_details?.sub_order_items]);

  const { forOnlyBarcode } = useFormValidations();

  const formHook = useForm<IQCPackpropsInterface>({
    mode: "onChange",
    defaultValues: {
      match: false,
      products: [],
    },
  });

  const normalProductsFormHook = useFieldArray({
    name: "products",
    control: formHook.control,
  });

  useEffect(() => {
    if (orders?.order_details?.sub_order_items) {
      normalProductsFormHook.replace(
        orders?.order_details?.sub_order_items?.map((item) => ({
          ...item,
          checkbox: false,
          inputqty: null,
          inputbarcode: null,
          inputexpiry: null,
          inputexpiries: [],
        })),
      );
      setValidatedExpiryByIndex({});
      setValidatingExpiryByIndex({});
      setRepeatExpiryConfirmation(null);
      setIsRepeatConfirming(false);
      setGiftCardQty("");
      attemptedExpirySignatureRef.current = {};
    }
  }, [orders?.order_details?.sub_order_items?.length]);

  const cancelButtonRef = useRef(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      dispatch(setIsUserPopupModalActive(true));
    } else {
      dispatch(setIsUserPopupModalActive(false));
    }
  }, [open]);

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // read query params
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const onSubmit = (data: IQCPackpropsInterface, e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isExpiryReadyForSubmit) {
      return;
    }

    const mismatches = data?.products?.map((item) => ({
      mismatch: parseInt(item.inputqty) !== item.quantity_to_pick,
    }));
    const hasMismatch = mismatches?.find((item) => item?.mismatch === true);
    const hasMismatchValue = hasMismatch?.mismatch || false;

    formHook.setValue("match", hasMismatchValue ? true : false);

    // Check for barcode mismatches with support for string | string[]
    const barcodeMismatch = data.products.some((product, index) => {
      const inputQty = parseInt(formHook.getValues(`products.${index}.inputqty` as const) as any);
      const inputBarcode = formHook.getValues(`products.${index}.inputbarcode` as const);
      const productBarcodes = normalizeBarcode(product.bar_code);

      // Skip validation if no quantity or no barcode defined in product
      if (!inputQty || inputQty === 0) return false;
      if (productBarcodes.length === 0) return false;

      return !productBarcodes.includes(inputBarcode || "");
    });

    if (barcodeMismatch) {
      toast.error("There is a barcode mismatch");
      return;
    }

    if (data.partial && hasMismatchValue) {
      const mismatchedProducts = data?.products?.filter((product) => {
        const inputQty = parseInt(product?.inputqty);
        const quantityToPick = product?.quantity_to_pick;
        return inputQty !== quantityToPick;
      });

      const updatedMismatchedProducts = mismatchedProducts?.map((product) => ({
        ...product,
        quantity_to_pick: product.quantity_to_pick - parseInt(product.inputqty),
      }));

      const productsWithoutExtraProps = updatedMismatchedProducts?.map(
        ({ checkbox, inputqty, inputbarcode, inputexpiry, inputexpiries, ...rest }) => rest,
      );

      const constructedBody: IOderManagementActionBody = {
        sub_order_id: orders?.order_details?.sub_order_id,
        internal_order_status: "ready_for_pickup",
        missing_order_items: productsWithoutExtraProps as any,
      };
      const statsBody: IOrderManagementStatsFilterBody = {
        start_date: orderFilter?.start_date,
        end_date: orderFilter?.end_date,
        clinic_id: orderFilter?.clinic_id?.split(","),
      };
      const requestBody: IOrderManagementFilterBody = {
        clinic_id: orderFilter?.clinic_id?.split(","),
        start_date: orderFilter?.start_date,
        end_date: orderFilter?.end_date,
        order_status: "",
        source: orderModule,
        // ...(orderSource && { order_source: orderSource }),
        // page_number: page,
        // page_size: 20,
        // ...(isCentralRole && { for_central: "true" }),
      };
      updateOrdersAPI(constructedBody).then(({ status }) => {
        if (status) {
          isDayClosing &&
            navigate(
              `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
            );
          fetchOrderListAPI(requestBody);
          fetchStatsAPI(statsBody);
          setOpen(false);
        }
      });
    } else if (!hasMismatchValue && !data.partial) {
      const constructedBody: IOderManagementActionBody = {
        sub_order_id: orders?.order_details?.sub_order_id,
        internal_order_status: "ready_for_pickup",
      };
      const statsBody: IOrderManagementStatsFilterBody = {
        start_date: orderFilter?.start_date,
        end_date: orderFilter?.end_date,
        clinic_id: orderFilter?.clinic_id?.split(","),
      };
      const requestBody: IOrderManagementFilterBody = {
        clinic_id: orderFilter?.clinic_id?.split(","),
        start_date: orderFilter?.start_date,
        end_date: orderFilter?.end_date,
        order_status: "",
        source: orderModule,
        // ...(orderSource && { order_source: orderSource }),
        // page_number: page,
        // page_size: 20,
        // ...(isCentralRole && { for_central: "true" }),
      };
      updateOrdersAPI(constructedBody).then(({ status }) => {
        if (status) {
          isDayClosing &&
            navigate(
              `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
            );
          fetchOrderListAPI(requestBody);
          fetchStatsAPI(statsBody);
          setOpen(false);
        }
      });
    } else {
      return null;
    }
  };

  useEffect(() => {
    const partial = formHook.getValues("partial");
    if (!partial) {
      formHook.setValue("match", false);
    }
  }, [formHook.watch("partial")]);

  const handleBarcodeScan = (barcode: string, e: any) => {
    e.preventDefault();
    const data = normalProductsFormHook.fields;

    // Find product by barcode (supports string | string[])
    const index = data?.findIndex((item) => normalizeBarcode(item?.bar_code).includes(barcode));

    if (index === -1) {
      formHook.setValue("scanner", "");
      toast.error("Wrong barcode scanned, Please recheck");
      return null;
    }

    const field = data[index];
    const inputQty = parseInt(field?.inputqty) || 0;

    // Already scanned required quantity
    if (inputQty >= field?.quantity_to_pick) {
      formHook.setValue("scanner", "");
      toast.error("Alert: More than required products scanned");
      return;
    }

    const newInputQty = inputQty + 1;
    normalProductsFormHook.update(index, {
      ...field,
      inputqty: newInputQty,
      inputbarcode: barcode,
    });
    formHook.trigger(`products.${index}.inputqty`);

    const productBarcodes = normalizeBarcode(field?.bar_code);
    const isBarcodeMatch = productBarcodes.includes(barcode);
    const hasBeenScannedBefore = scannedItems.has(index);

    if (isBarcodeMatch && !hasBeenScannedBefore) {
      setHighlightedRows((prev) => [...prev, index]);
      setScannedItems((prev) => new Set([...prev, index]));
      setLastScannedIndex(index);

      setTimeout(() => {
        setHighlightedRows((prev) => prev.filter((i) => i !== index));
      }, 1000);
    }

    formHook.setValue("scanner", "");
  };

  const debouncedHandleBarcodeScan = debounce(handleBarcodeScan, 300);

  // Auto-scroll to last scanned item
  useEffect(() => {
    if (lastScannedIndex !== null && scrollContainerRef.current) {
      const itemElement = scrollContainerRef.current.children[lastScannedIndex];
      if (itemElement) {
        itemElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [lastScannedIndex]);

  //Excalation
  const handelRaiseExcalation = () => {
    const constructedBody: IOderManagementActionBody = {
      sub_order_id: orders?.order_details?.sub_order_id,
    };
    const statsBody: IOrderManagementStatsFilterBody = {
      start_date: orderFilter?.start_date,
      end_date: orderFilter?.end_date,
      clinic_id: orderFilter?.clinic_id?.split(","),
    };
    const requestBody: IOrderManagementFilterBody = {
      clinic_id: orderFilter?.clinic_id?.split(","),
      start_date: orderFilter?.start_date,
      end_date: orderFilter?.end_date,
      order_status: "",
      source: orderModule,
      // ...(orderSource && { order_source: orderSource }),
      // page_number: page,
      // page_size: 20,
      // ...(isCentralRole && { for_central: "true" }),
    };
    exclationOrdersAPI(constructedBody).then(({ status }) => {
      if (status) {
        fetchOrderListAPI(requestBody);
        fetchStatsAPI(statsBody);
        setOpen(false);
      }
    });
  };

  useEffect(() => {
    const productList = formHook.getValues();
    const areAllCheckboxesTrue = productList?.products?.every((item) => item.checkbox === true);
    if (areAllCheckboxesTrue) {
      setsIShowPertialOrder(true);
    } else {
      setsIShowPertialOrder(false);
    }
  }, [formHook.watch("expiry"), formHook.watch("lotNo")]);

  // Compute if there is any quantity mismatch
  const isQtyMismatch = normalProductsFormHook.fields.some((item, id) => {
    const inputQty = formHook.getValues(`products.${id}.inputqty`);
    if (inputQty === null || inputQty === "" || isNaN(parseInt(inputQty))) {
      return false; // treat as no mismatch if not filled
    }
    return parseInt(inputQty) !== item.quantity_to_pick;
  });

  // If any input quantity is smaller than the required quantity, hide partial order option
  // const hasLowerQtyThanToPick = normalProductsFormHook.fields.some((item, id) => {
  //   const inputQty = formHook.watch(`products.${id}.inputqty`);
  //   const numericQty = parseInt(inputQty as any);
  //   if (isNaN(numericQty)) {
  //     return item.quantity_to_pick > 0;
  //   }
  //   return numericQty < item.quantity_to_pick;
  // });

  // useEffect(() => {
  //   if (hasLowerQtyThanToPick && formHook.getValues("partial")) {
  //     formHook.setValue("partial", false);
  //   }
  // }, [hasLowerQtyThanToPick]);

  const isTransferOrder = orders?.order_details?.sub_order_id?.startsWith("TO");

  const validateExpiryForProduct = useCallback(
    (id: number, forceRepeat = false) => {
      if (!orders?.order_details) return;
      const item = normalProductsFormHook.fields?.[id];
      if (!item) return;

      const inputQty = Number.parseInt(String(formHook.getValues(`products.${id}.inputqty`) || 0), 10);
      const hasPositiveQty = !Number.isNaN(inputQty) && inputQty > 0;
      const inputBarcode = (formHook.getValues(`products.${id}.inputbarcode`) || "").trim();
      const inputExpiry = (formHook.getValues(`products.${id}.inputexpiry`) || "").trim();
      const extraExpiries = (formHook.getValues(`products.${id}.inputexpiries`) || []).map((value) =>
        (value || "").trim(),
      );
      const allExpiries = [inputExpiry, ...extraExpiries];

      const productBarcodes = normalizeBarcode(item?.bar_code);
      const hasNoBarcode = productBarcodes.length === 0;
      const isBarcodeMatched = productBarcodes.includes(inputBarcode);
      const isMedProduct = (item as any)?.med === true || (item as any)?.med === "true";
      const isExpiryFieldVisible = isMedProduct && hasPositiveQty && (hasNoBarcode || isBarcodeMatched);

      if (!isExpiryFieldVisible) {
        setValidatedExpiryByIndex((prev) => (prev[id] ? { ...prev, [id]: false } : prev));
        setValidatingExpiryByIndex((prev) => (prev[id] ? { ...prev, [id]: false } : prev));
        attemptedExpirySignatureRef.current[id] = "";
        return;
      }

      const hasCompleteAndValidExpiry =
        allExpiries.length > 0 &&
        allExpiries.every((expiry) => expiry.length > 0 && EXPIRY_DATE_REGEX.test(expiry));

      if (!hasCompleteAndValidExpiry) {
        setValidatedExpiryByIndex((prev) => (prev[id] ? { ...prev, [id]: false } : prev));
        attemptedExpirySignatureRef.current[id] = "";
        return;
      }

      const payload = {
        order_id: orders?.order_details?.order_id,
        sub_order_id: orders?.order_details?.sub_order_id,
        vetic_variant_id: item?.vetic_variant_id,
        mrp: Number(item?.mrp || 0),
        expiry_dates: allExpiries,
        ...(forceRepeat ? { is_repeat: true } : {}),
      };
      const signature = JSON.stringify(payload);
      if (!forceRepeat && attemptedExpirySignatureRef.current[id] === signature) return;
      if (repeatExpiryConfirmation?.id === id && repeatExpiryConfirmation.signature !== signature) {
        setRepeatExpiryConfirmation(null);
      }

      attemptedExpirySignatureRef.current[id] = signature;
      setValidatedExpiryByIndex((prev) => ({ ...prev, [id]: false }));
      setValidatingExpiryByIndex((prev) => ({ ...prev, [id]: true }));

      validateMedicineExpiryAPI(payload)
        .then((response) => {
          if (attemptedExpirySignatureRef.current[id] !== signature) return;
          if (response?.isRepeat && !forceRepeat) {
            setRepeatExpiryConfirmation({
              id,
              signature,
              payload: {
                order_id: orders?.order_details?.order_id,
                sub_order_id: orders?.order_details?.sub_order_id,
                vetic_variant_id: item?.vetic_variant_id,
                mrp: Number(item?.mrp || 0),
                expiry_dates: allExpiries,
              },
            });
            setValidatedExpiryByIndex((prev) => ({ ...prev, [id]: false }));
            return;
          }
          if (repeatExpiryConfirmation?.id === id) {
            setRepeatExpiryConfirmation(null);
          }
          setValidatedExpiryByIndex((prev) => ({ ...prev, [id]: Boolean(response?.status) }));
        })
        .finally(() => {
          if (attemptedExpirySignatureRef.current[id] !== signature) return;
          setValidatingExpiryByIndex((prev) => ({ ...prev, [id]: false }));
        });
    },
    [formHook, normalProductsFormHook.fields, orders?.order_details, repeatExpiryConfirmation, validateMedicineExpiryAPI],
  );

  useEffect(() => {
    normalProductsFormHook.fields.forEach((_, index) => validateExpiryForProduct(index));

    const subscription = formHook.watch((_, { name }) => {
      if (!name || !name.startsWith("products.")) return;
      if (
        !name.includes(".inputbarcode") &&
        !name.includes(".inputexpiry") &&
        !name.includes(".inputexpiries") &&
        !name.includes(".inputqty")
      ) {
        return;
      }

      const match = name.match(/products\.(\d+)\./);
      if (!match) return;
      const index = parseInt(match[1], 10);
      if (Number.isNaN(index)) return;
      validateExpiryForProduct(index);
    });

    return () => subscription.unsubscribe();
  }, [formHook, normalProductsFormHook.fields, validateExpiryForProduct]);

  const handleRepeatExpiryRetry = () => {
    if (!repeatExpiryConfirmation) return;
    const targetIndex = repeatExpiryConfirmation.id;
    formHook.setValue(`products.${targetIndex}.inputexpiry`, "", { shouldDirty: true });
    formHook.setValue(`products.${targetIndex}.inputexpiries`, [], { shouldDirty: true });
    attemptedExpirySignatureRef.current[repeatExpiryConfirmation.id] = "";
    setValidatedExpiryByIndex((prev) => ({ ...prev, [repeatExpiryConfirmation.id]: false }));
    setValidatingExpiryByIndex((prev) => ({ ...prev, [repeatExpiryConfirmation.id]: false }));
    setRepeatExpiryConfirmation(null);
  };

  const handleRepeatExpiryConfirm = () => {
    if (!repeatExpiryConfirmation || isRepeatConfirming) return;
    setIsRepeatConfirming(true);

    validateMedicineExpiryAPI({
      ...repeatExpiryConfirmation.payload,
      is_repeat: true,
    })
      .then((response) => {
        if (response?.status) {
          setValidatedExpiryByIndex((prev) => ({ ...prev, [repeatExpiryConfirmation.id]: true }));
          setRepeatExpiryConfirmation(null);
          return;
        }
        setValidatedExpiryByIndex((prev) => ({ ...prev, [repeatExpiryConfirmation.id]: false }));
      })
      .finally(() => {
        setIsRepeatConfirming(false);
      });
  };

  const hasVisibleExpiryField = normalProductsFormHook.fields.some((item, id) => {
    const inputQty = Number.parseInt(String(formHook.getValues(`products.${id}.inputqty`) || 0), 10);
    const hasPositiveQty = !Number.isNaN(inputQty) && inputQty > 0;
    const inputBarcode = (formHook.getValues(`products.${id}.inputbarcode`) || "").trim();
    const isMedProduct = (item as any)?.med === true || (item as any)?.med === "true";
    const productBarcodes = normalizeBarcode(item?.bar_code);
    const hasNoBarcode = productBarcodes.length === 0;
    return isMedProduct && hasPositiveQty && (hasNoBarcode || productBarcodes.includes(inputBarcode));
  });
  const hasExpiryRequirement = normalProductsFormHook.fields.some((item) => {
    const isMedProduct = (item as any)?.med === true || (item as any)?.med === "true";
    return isMedProduct;
  });
  const isExpiryReadyForSubmit = normalProductsFormHook.fields.every((item, id) => {
    const productValues = formHook.getValues(`products.${id}`);
    const inputQty = Number.parseInt(String(productValues?.inputqty || 0), 10);
    const hasPositiveQty = !Number.isNaN(inputQty) && inputQty > 0;
    const inputBarcode = (productValues?.inputbarcode || "").trim();
    const inputExpiry = (productValues?.inputexpiry || "").trim();
    const extraExpiries = (productValues?.inputexpiries || []).map((value) => (value || "").trim());
    const allExpiries = [inputExpiry, ...extraExpiries];
    const productBarcodes = normalizeBarcode(item?.bar_code);
    const hasNoBarcode = productBarcodes.length === 0;
    const isBarcodeMatched = productBarcodes.includes(inputBarcode);
    const isMedProduct = (item as any)?.med === true || (item as any)?.med === "true";
    if (!isMedProduct) return true;
    if (!hasPositiveQty) return true;
    if (!hasNoBarcode && !isBarcodeMatched) return false;
    if (validatingExpiryByIndex[id]) return false;
    return (
      allExpiries.length > 0 &&
      allExpiries.every((expiry) => expiry.length > 0 && EXPIRY_DATE_REGEX.test(expiry)) &&
      validatedExpiryByIndex[id] === true
    );
  });
  const canSubmitOrder =
    formHook.watch("expiry") && (!isQtyMismatch || formHook.watch("partial")) && isExpiryReadyForSubmit;
  const isExpiryCheckboxDisabled = hasExpiryRequirement && !isExpiryReadyForSubmit;
  // Highlight rows when barcode fields match product barcode(s)
  useEffect(() => {
    const subscription = formHook.watch((value, { name }) => {
      // Only handle barcode changes
      if (!name || !name.startsWith("products.") || !name.endsWith(".inputbarcode")) return;

      const match = name.match(/products\.(\d+)\.inputbarcode/);
      if (!match) return;
      const index = parseInt(match[1]);

      const item = normalProductsFormHook.fields[index];
      const inputBarcode = value?.products?.[index]?.inputbarcode;

      const productBarcodes = normalizeBarcode(item?.bar_code);
      if (productBarcodes.includes(inputBarcode) && !highlightedRows.includes(index)) {
        setHighlightedRows((prev) => [...prev, index]);
        setTimeout(() => {
          setHighlightedRows((prev) => prev.filter((i) => i !== index));
        }, 1000);
      }
    });

    // Cleanup on unmount
    return () => subscription.unsubscribe();
  }, [formHook, normalProductsFormHook.fields, highlightedRows]);
  useEffect(() => {
    if (!isQtyMismatch) {
      formHook.setValue("partial", false);
    }
  }, [isQtyMismatch, formHook]);

  const renderExpiryInputs = ({
    id,
    isExpiryFieldVisible,
    extraExpiries,
    keyPrefix,
    containerClassName = "flex-1 relative",
    showLabel = false,
  }: {
    id: number;
    isExpiryFieldVisible: boolean;
    extraExpiries: string[];
    keyPrefix: string;
    containerClassName?: string;
    showLabel?: boolean;
  }) => {
    if (!isExpiryFieldVisible) return null;

    const handleAddExpiryField = () => {
      const currentExpiries = formHook.getValues(`products.${id}.inputexpiries`) || [];
      const primaryExpiry = (formHook.getValues(`products.${id}.inputexpiry`) || "").trim();
      const lastExtraIndex = currentExpiries.length - 1;
      const lastExtraExpiry = lastExtraIndex >= 0 ? (currentExpiries[lastExtraIndex] || "").trim() : "";
      const lastVisibleExpiryValue = lastExtraIndex >= 0 ? lastExtraExpiry : primaryExpiry;

      if (!lastVisibleExpiryValue) {
        return;
      }

      formHook.setValue(`products.${id}.inputexpiries`, [...currentExpiries, ""], {
        shouldDirty: true,
      });
    };

    return (
      <div className={containerClassName}>
        <div className="flex flex-col gap-2">
          <div className="relative">
            {showLabel && <p className="absolute -top-6 left-0 text-[11px] font-medium">Expiry</p>}
            <div className="flex items-center gap-2">
              {(() => {
                const primaryExpiryValue = formHook.watch(`products.${id}.inputexpiry`) || "";
                const isPrimaryExpiryPartInvalid = hasInvalidExpiryParts(primaryExpiryValue);
                return (
              <Input
                className="text-xs flex-1"
                inputClassName={`h-9 ${isPrimaryExpiryPartInvalid ? "!text-[#DC2626]" : ""}`}
                placeholder="DD/MM/YYYY"
                maxLength={10}
                inputMode="numeric"
                register={formHook.register(`products.${id}.inputexpiry`, {
                  onChange: (e) => {
                    const rawValue = (e.target as HTMLInputElement).value || "";
                    const nativeInputType = (e.nativeEvent as InputEvent | undefined)?.inputType || "";
                    const isDeleting = nativeInputType.startsWith("delete");
                    const formattedValue = formatExpiryDateInput(rawValue, !isDeleting);
                    (e.target as HTMLInputElement).value = formattedValue;
                  },
                })}
                fieldError={null}
                errorMessages={[]}
              />
                );
              })()}
              <button
                type="button"
                onClick={handleAddExpiryField}
                className="text-[#193A6A] hover:text-[#0f2748]"
                aria-label="Add expiry"
              >
                <PlusCircleIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {extraExpiries.map((_, extraIndex) => (
            <div key={`${id}-${keyPrefix}-${extraIndex}`} className="flex items-center gap-2">
              {(() => {
                const extraExpiryValue = formHook.watch(`products.${id}.inputexpiries.${extraIndex}` as const) || "";
                const isExtraExpiryPartInvalid = hasInvalidExpiryParts(extraExpiryValue);
                return (
              <Input
                className="text-xs flex-1"
                inputClassName={`h-9 ${isExtraExpiryPartInvalid ? "!text-[#DC2626]" : ""}`}
                placeholder="DD/MM/YYYY"
                maxLength={10}
                inputMode="numeric"
                register={formHook.register(`products.${id}.inputexpiries.${extraIndex}` as const, {
                  onChange: (e) => {
                    const rawValue = (e.target as HTMLInputElement).value || "";
                    const nativeInputType = (e.nativeEvent as InputEvent | undefined)?.inputType || "";
                    const isDeleting = nativeInputType.startsWith("delete");
                    const formattedValue = formatExpiryDateInput(rawValue, !isDeleting);
                    (e.target as HTMLInputElement).value = formattedValue;
                  },
                })}
                fieldError={null}
                errorMessages={[]}
              />
                );
              })()}
              <button
                type="button"
                onClick={() => {
                  const currentExpiries = formHook.getValues(`products.${id}.inputexpiries`) || [];
                  formHook.setValue(
                    `products.${id}.inputexpiries`,
                    currentExpiries.filter((_, index) => index !== extraIndex),
                    { shouldDirty: true },
                  );
                }}
                className="text-[#b42318] hover:text-[#912018]"
                aria-label="Remove expiry"
              >
                <MinusCircleIcon className="h-6 w-6" />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[80]" initialFocus={cancelButtonRef} onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[80] w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={`relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all 
                
                ${hasVisibleExpiryField ? "sm:max-w-[78rem]" : "sm:max-w-6xl"}
                sm:w-full  sm:p-6`}
                >
                  <div className="flex justify-between items-center">
                    {" "}
                    <div className="items-center flex">
                      <p className="text-base font-semibold">Order Details ({orders?.order_details?.sub_order_id})</p>
                      <p className="text-sm font-semibold bg-[#F5F5F5] text-[#6B7280] p-1 px-2 ml-4 rounded-md">
                        {orders?.order_details?.sub_order_items?.length} Items
                      </p>
                    </div>
                    <div className="flex justify-center">
                      <Input
                        className="text-xs w-60"
                        placeholder="Scan Barcode"
                        register={formHook.register("scanner", {
                          required: false,
                          ...forOnlyBarcode.validations,
                        })}
                        fieldError={formHook?.formState?.errors?.scanner}
                        errorMessages={[{ message: "Barcode is required", type: "required" }, forOnlyBarcode.errors]}
                        onChange={(e) => {
                          const barcode = (e.target as HTMLInputElement).value;
                          e.preventDefault();
                          e.stopPropagation();
                          if (barcode !== "0") {
                            debouncedHandleBarcodeScan(barcode, e);
                          }
                          if (e.target instanceof HTMLInputElement) {
                            e.target.focus();
                          }
                        }}
                      />{" "}
                      <div className="relative ml-6">
                        <div className="p-4">
                          <XMarkIcon
                            className="w-6 h-6 cursor-pointer absolute top-[-15px] right-[-15px] text-black rounded-full bg-[#808DA01A] p-1"
                            onClick={() => setOpen(false)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <form onSubmit={formHook.handleSubmit(onSubmit)}>
                    <div
                      className={`${orders?.order_details?.sub_order_items?.length > 3 ? "h-96 overflow-auto" : "h-auto"} w-full`}
                    >
                      <div className="rounded-xl bg-[#F5F5F5] mt-2 p-[2px]">
                        <div
                          ref={scrollContainerRef}
                          className={`${
                            normalProductsFormHook.fields.length > 2
                              ? "max-h-96 overflow-y-auto show-scrollbar"
                              : "max-h-full"
                          }`}
                        >
                          <table className="w-full table-fixed border-separate border-spacing-0">
                            <colgroup>
                              <col className="w-[12%]" />
                              <col className="w-[16%]" />
                              <col className="w-[14%]" />
                              <col className="w-[9%]" />
                              <col className="w-[11%]" />
                              <col className={hasVisibleExpiryField ? "w-[38%]" : "w-[20%]"} />
                              <col className="w-[8%]" />
                            </colgroup>
                            <thead className="sticky top-0 z-10 bg-[#F5F5F5]">
                              <tr className="text-xs text-left">
                                <th className="px-4 py-3 font-normal">Item Image</th>
                                <th className="px-4 py-3 font-normal">Item Name</th>
                                <th className="px-4 py-3 font-normal">Lot_Number | MRP</th>
                                <th className="px-4 py-3 font-normal">Qty</th>
                                <th className="px-4 py-3 font-normal">Packed Qty</th>
                                <th className="px-4 py-3 font-normal">
                                  <div className="grid grid-cols-[200px_200px] items-center gap-2 w-max">
                                    <p>Barcode</p>
                                    {hasVisibleExpiryField && <p>Expiry</p>}
                                  </div>
                                </th>
                                <th className="px-4 py-3 font-normal">Packed</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white">
                              {normalProductsFormHook.fields.map((item, id) => {
                            const inputQty = parseInt(formHook.watch(`products.${id}.inputqty`) || "0");
                            const inputBarcode = formHook.watch(`products.${id}.inputbarcode`) || "";
                            const inputExpiry = (formHook.watch(`products.${id}.inputexpiry`) || "").trim();
                            const extraExpiries = (formHook.watch(`products.${id}.inputexpiries`) || []).map((value) =>
                              (value || "").trim(),
                            );
                            const productBarcodes = normalizeBarcode(item.bar_code);
                            const hasNoBarcode = productBarcodes.length === 0;
                            const isBarcodeMatched = productBarcodes.includes(inputBarcode);
                            const isMedProduct = (item as any)?.med === true || (item as any)?.med === "true";
                            const isExpiryFieldVisible = isMedProduct && inputQty > 0 && (hasNoBarcode || isBarcodeMatched);
                            const allExpiries = [inputExpiry, ...extraExpiries];
                            const isExpirySatisfied =
                              !isExpiryFieldVisible ||
                              (allExpiries.length > 0 &&
                                allExpiries.every((expiry) => expiry.length > 0 && EXPIRY_DATE_REGEX.test(expiry)) &&
                                validatedExpiryByIndex[id] === true);
                            let isChecked = item.quantity_to_pick === inputQty;

                            if (productBarcodes.length > 0) {
                              isChecked = isChecked && isBarcodeMatched;
                            }
                            isChecked = isChecked && isExpirySatisfied;

                            formHook.setValue(`products.${id}.checkbox`, isChecked);

                            return (
                              <Fragment key={item.id}>
                                <tr
                                  className={`text-xs transition-colors duration-1000 ease-in-out ${
                                    highlightedRows.includes(id) ? "bg-green-300" : "bg-white"
                                  }`}
                                >
                                  <td className={`py-2 align-middle ${item.mrp_check ? "" : "border-b"}`}>
                                    <ImgPreview data={orders?.order_details?.sub_order_items} imageitem={item} />
                                  </td>
                                  <td className={`px-4 py-2 align-middle text-sm ${item.mrp_check ? "" : "border-b"}`}>
                                    <p className="cursor-pointer" onClick={() => setImageitem(item)}>
                                      {formHook.getValues(`products.${id}.item_name`)}
                                    </p>
                                  </td>
                                  <td className={`px-4 py-2 align-middle text-xs text-[#202223] ${item.mrp_check ? "" : "border-b"}`}>
                                    <p>{`${item.lot_number || item.lot_no || item.lot || "-"} | ₹${item.mrp ?? "-"}`}</p>
                                  </td>
                                  <td className={`px-4 py-2 align-middle ${item.mrp_check ? "" : "border-b"}`}>
                                    <div className="flex justify-start items-center text-sm">
                                      <div className="bg-[#F4F4F4] rounded-md w-10 h-10 flex justify-center items-center">
                                        {formHook.getValues(`products.${id}.quantity_to_pick`)}
                                      </div>
                                    </div>
                                  </td>
                                  <td className={`px-4 py-2 align-middle ${item.mrp_check ? "" : "border-b"}`}>
                                    <div className="relative">
                                    <Input
                                      className="text-xs"
                                      placeholder="Enter Qty"
                                      type="number"
                                      register={formHook.register(`products.${id}.inputqty`, {
                                        required: true,
                                        min: 0,
                                        max: item.quantity_to_pick,
                                        ...forOnlyBarcode.validations,
                                        onChange: (e) => {
                                          let val = e.target.value;
                                          if (val === "") {
                                            formHook.setValue(`products.${id}.inputqty`, "", {
                                              shouldValidate: true,
                                              shouldDirty: true,
                                            });
                                            return;
                                          }
                                          val = parseInt(val);
                                          if (val < 0) val = 0;
                                          if (val > item.quantity_to_pick) val = item.quantity_to_pick;
                                          formHook.setValue(`products.${id}.inputqty`, val, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                          });
                                        },
                                      })}
                                      fieldError={formHook?.formState?.errors?.products?.[id]?.inputqty || null}
                                      errorMessages={[
                                        { message: "Quantity is required", type: "required" },
                                        { message: `Cannot exceed ${item.quantity_to_pick}`, type: "max" },
                                        { message: `Cannot be negative`, type: "min" },
                                        forOnlyBarcode.errors,
                                      ]}
                                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                        if (e.key === "-" || e.key === "e") {
                                          e.preventDefault();
                                        }
                                      }}
                                    />

                                    {item.quantity_to_pick !== inputQty && inputQty > 0 && (
                                      <p className="text-red-500 text-[9px] absolute -bottom-4 left-1">
                                        Quantity Mismatch
                                      </p>
                                    )}
                                    </div>
                                  </td>
                                  <td className={`px-4 py-2 align-middle ${item.mrp_check ? "" : "border-b"}`}>
                                    <div className="relative">
                                    <div className="grid grid-cols-[200px_200px] items-center gap-2 w-max">
                                      <div className="w-[200px] relative">
                                        {!hasNoBarcode ? (
                                          <>
                                            <Input
                                              className="text-xs w-[200px]"
                                              inputClassName="h-9"
                                              placeholder="Enter Barcode"
                                              register={formHook.register(`products.${id}.inputbarcode`, {
                                                required: {
                                                  value: inputQty > 0,
                                                  message: "Barcode is required if quantity is more than 0",
                                                },
                                                onChange: (e) => {
                                                  const barcodeValue = (e.target as HTMLInputElement).value?.trim();
                                                  const isMatchedBarcode = normalizeBarcode(item.bar_code).includes(
                                                    barcodeValue,
                                                  );
                                                  if (!isMatchedBarcode) {
                                                    formHook.setValue(`products.${id}.inputexpiry`, "");
                                                    formHook.setValue(`products.${id}.inputexpiries`, []);
                                                  }
                                                },
                                              })}
                                              fieldError={formHook?.formState?.errors?.products?.[id]?.inputbarcode || null}
                                              errorMessages={[
                                                { message: "Barcode is required", type: "required" },
                                                forOnlyBarcode.errors,
                                              ]}
                                            />
                                            {!hasNoBarcode &&
                                              !isBarcodeMatched &&
                                              inputBarcode &&
                                              inputBarcode.length > 0 && (
                                                <p className="text-red-500 text-[9px] absolute top-full mt-1 left-1">
                                                  Barcode Mismatch
                                                </p>
                                              )}
                                          </>
                                        ) : (
                                          <div className="h-9 px-1 flex items-center">
                                            <p className="text-xs text-gray-500 whitespace-nowrap leading-none">Item has no Barcode</p>
                                          </div>
                                        )}
                                      </div>

                                      <div className="w-[200px]">
                                        {renderExpiryInputs({
                                          id,
                                          isExpiryFieldVisible,
                                          extraExpiries,
                                          keyPrefix: !hasNoBarcode ? "expiry" : "no-barcode-expiry",
                                          containerClassName: "w-[200px] relative",
                                          showLabel: false,
                                        })}
                                        {!isExpiryFieldVisible && <div className="h-9" />}
                                      </div>
                                    </div>

                                    <div className="absolute top-full left-0 min-h-[12px] mt-1">
                                      {isExpiryFieldVisible && validatingExpiryByIndex[id] && (
                                        <p className="text-[#475467] text-[9px]">Validating expiry...</p>
                                      )}
                                    </div>
                                    </div>
                                  </td>
                                  <td className={`px-4 py-2 align-middle ${item.mrp_check ? "" : "border-b"}`}>
                                    <div className="flex justify-start">
                                    <div
                                      onClick={() => {
                                        formHook.setValue(`products.${id}.checkbox`, !isChecked);
                                      }}
                                      className={`w-6 h-6 flex items-center justify-center rounded-full ${
                                        isChecked ? "bg-green-600 border-green-600" : ""
                                      }`}
                                    >
                                      {isChecked ? (
                                        <svg
                                          className="w-4 h-4 text-white"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth={3}
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      ) : (
                                        <span className="text-gray-400 font-semibold text-lg"></span>
                                      )}
                                    </div>
                                  </div>
                                  </td>
                                </tr>
                                {Boolean(item.mrp_check) && (
                                  <tr className="bg-white">
                                    <td colSpan={7} className="border-b px-4 pb-2 text-xs">
                                      <span className="inline-flex w-max whitespace-nowrap rounded-full bg-[#FEF3F2] px-3 py-1 text-xs font-medium text-[#B42318]">
                                        {item.mrp_check}
                                      </span>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })}
                              {hasGiftItem && (
                                <GiftCardRow
                                  qty={giftCardQty}
                                  onQtyChange={setGiftCardQty}
                                  onQtyClick={logGiftCardQtyClick}
                                />
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 mt-4 bg-[#FAFAFA] rounded-xl">
                      <div className="flex gap-2">
                        <SimpleCheckboxWithError
                          register={formHook.register(`expiry`, {
                            required: true,
                            ...forOnlyBarcode.validations,
                          })}
                          fieldError={formHook?.formState?.errors?.expiry}
                          errorMessages={[{ message: "Quantity is required", type: "required" }, forOnlyBarcode.errors]}
                          onChange={() => {
                            formHook.setValue("expiry", !formHook.getValues("expiry"));
                          }}
                          checked={formHook.watch("expiry")}
                          id="expiry"
                          colorClassName={`text-[#202223] ${
                            isExpiryCheckboxDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                          }`}
                          className="col-span-1"
                          disabled={isExpiryCheckboxDisabled}
                        />
                        <label
                          htmlFor="expiry"
                          className={`ml-2 ${isExpiryCheckboxDisabled ? "text-gray-400" : "text-[#202223]"}`}
                        >
                          <p className="text-sm">Product Expiry Check</p>
                          <p className="text-xs">I confirm that expiry dates are checked and accept punitive action under the Zero Tolerance Policy for any expired product handed over.</p>
                        </label>
                      </div>
                      {/* Partial Order Section */}
                      <div className="flex items-center justify-between gap-4 mt-3">
                        <div>
                          {!orders?.order_details?.is_partial_sub_order && !isTransferOrder && (
                            <div className={`flex gap-2 ${!isQtyMismatch ? "opacity-50" : ""}`}>
                              <SimpleCheckboxWithError
                                register={formHook.register(`partial`, { required: false })}
                                fieldError={formHook?.formState?.errors?.partial}
                                errorMessages={[{ message: "Quantity is required", type: "required" }]}
                                onChange={() => {
                                  const currentPartialValue = formHook.getValues("partial");
                                  formHook.setValue("partial", !currentPartialValue);
                                }}
                                id="partial"
                                colorClassName="cursor-pointer"
                                className="col-span-1"
                                disabled={!isQtyMismatch}
                              />

                              <label
                                htmlFor="partial"
                                className={`ml-2 ${isQtyMismatch ? "text-black" : "text-gray-400"}`}
                              >
                                <p className="text-sm">Partial Order</p>
                                <p className="text-xs">I accept the penalty on sending this partial order</p>
                              </label>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-between gap-4">
                          {orders?.order_details?.is_partial_sub_order && (
                            <button
                              className="rounded-md bg-[#193A6A] px-2.5 py-1.5 w-1/5  font-medium cursor-pointer text-white shadow-sm text-sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const eee = e.target as HTMLInputElement;
                                if (eee?.value !== "0") {
                                  handelRaiseExcalation();
                                }
                              }}
                            >
                              Raise Escalation
                            </button>
                          )}
                          {/* Submit Button */}
                          <div className="flex justify-end w-full gap-3 mt-4">
                            <input
                              type="submit"
                              className={`rounded-md px-10 py-2 font-medium shadow-sm text-white text-sm ${
                                canSubmitOrder
                                  ? "bg-black cursor-pointer opacity-100"
                                  : "bg-gray-400 cursor-not-allowed opacity-50"
                              }`}
                              value={formHook.watch("partial") ? "Send Partial Order" : "Done"}
                              disabled={!canSubmitOrder}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <ExpiryRepeatWarningModal
        open={Boolean(repeatExpiryConfirmation)}
        onRetry={handleRepeatExpiryRetry}
        onConfirm={handleRepeatExpiryConfirm}
        confirmLoading={isRepeatConfirming}
      />
    </div>
  );
};
export default PackButtonComponent;
